import React, { useEffect, useState } from "react";
import clsx from "clsx";
import {
  PageContainer,
  PageContent,
  PageError,
  PageLoading,
} from "apps/user/learner/components/page-container";
import styles from "./home.module.scss";

import { LearnerGroupSession, LearnerRecording } from "../../api/types";
import AssessmentItemCard from "../../components/cards/AssessmentItemCard";
import { Carousel } from "../../components/carousel";
import useFetchHomeData from "../../hooks/use-fetch-home-data";
import FeaturedLayout from "../../components/featured-layout";
import DatedList from "../../components/dated-list";
import PopularContent from "../../components/popular-content";
import { getSessionQuote } from "./util";
import { ReactComponent as LotusIcon } from "Assets/icon/icon-lotus.svg";
import { MobileAppSignpostCard } from "../../components/mobile-app-signpost-card";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "components/ui";
import {
  IndexScoreCard,
  OriScoreColumn,
  ScoreBreakdownCard,
} from "../../components/cards/stat-cards";
import CtaBanner from "../../components/cta-banner";
import HealthBingoModal from "../../components/modals/HealthBingoModal";
import DecemberDoorsBand, {
  TUCKED_UNDER_BAND_CLASS,
} from "../../components/december-doors-band";
import useFeatureFlags from "../../hooks/use-feature-flags";
import useWebOriEnabled from "../../hooks/use-web-ori-enabled";
import { useTranslation } from "lib/i18n/use-translation";

const Home = () => {
  const { t } = useTranslation(["home", "feedback"]);
  const { loading, error, data } = useFetchHomeData();
  const { isEnabled } = useFeatureFlags();
  const [modalDismissed, setModalDismissed] = useState(false);
  const showHealthBingo = isEnabled("health_bingo");
  const { enabled: showOriWeb } = useWebOriEnabled();

  const location = useLocation();
  const navigate = useNavigate();
  const { showFeedbackSubmittedToast = false } = location.state || {};
  const { openToast } = useToast();

  useEffect(() => {
    if (showFeedbackSubmittedToast) {
      openToast({
        messageHeader: t("feedback:toast.title"),
        message: t("feedback:toast.message"),
        size: "medium",
      });

      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [showFeedbackSubmittedToast, location.pathname, navigate, openToast, t]);

  const randomQuote = getSessionQuote();

  if (loading) {
    return (
      <PageContainer>
        <PageContent maxWidth="lg">
          <PageLoading />
        </PageContent>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PageContent maxWidth="lg">
          <PageError />
        </PageContent>
      </PageContainer>
    );
  }

  if (!data) {
    return null;
  }

  const lowestPillarLabel = t("home:recommended.indexSubtitle");

  const renderTopSection = () => {
    const daysUntilNextAssessment =
      data?.learner.assessmentSchedule?.daysUntilNextAssessmentDue ?? null;
    const daysText =
      daysUntilNextAssessment === null
        ? t("home:assessmentDue.unknown")
        : t("home:assessmentDue.days", { count: daysUntilNextAssessment });

    const getAssessmentCard = () => {
      if (!data?.learner.assessmentSchedule?.isNextAssessmentDue) {
        return [];
      }

      return [
        <AssessmentItemCard
          key="assessment-item-card"
          link={"/user/woneindex/assessment"}
          assessmentSchedule={data.learner.assessmentSchedule}
        />,
      ];
    };

    return (
      <div className={clsx(styles["hero-banner"], TUCKED_UNDER_BAND_CLASS)}>
        <Carousel
          showCarouselButtons={false}
          title={
            <div className={styles["hero-banner-title-wrapper"]}>
              <h2 className={styles["greeting-title-hero"]}>
                {data.learner.screens.home.titleHtml}
              </h2>
              {!data?.learner?.assessmentSchedule?.isNextAssessmentDue &&
                daysUntilNextAssessment !== null &&
                daysUntilNextAssessment <= 15 && (
                  <div className={styles["assessment-due-info"]}>
                    <p className={styles["assessment-due-text"]}>
                      {t("home:assessmentDue.label")}
                    </p>
                    <div className={styles["assessment-due-count"]}>
                      <p className={styles["assessment-due-count-text"]}>
                        {daysText}
                      </p>
                    </div>
                  </div>
                )}
            </div>
          }
          items={[
            ...getAssessmentCard(),
            ...(data.learner.indexScores?.takenAt
              ? [
                  showOriWeb ? (
                    <OriScoreColumn
                      overallIndexScore={data.learner.indexScores?.overallIndex}
                      summary={data.learner.indexScores?.summary}
                      takenAt={data.learner.indexScores?.takenAt}
                      source="Ori Coach Card (Home)"
                    />
                  ) : (
                    <IndexScoreCard
                      overallIndexScore={data.learner.indexScores?.overallIndex}
                      summary={data.learner.indexScores?.summary}
                      takenAt={data.learner.indexScores?.takenAt}
                      onBreakdownClick={() => {
                        navigate("/user/woneindex");
                      }}
                      onExplanationClick={() => {
                        navigate("/user/articles/58");
                      }}
                    />
                  ),
                ]
              : []),
            <ScoreBreakdownCard
              domains={data.learner.indexScores?.domains}
              takenAt={data.learner.indexScores?.takenAt}
              onExplanationClick={() => {
                navigate("/user/articles/58");
              }}
            />,
            <MobileAppSignpostCard />,
          ]}
        />
      </div>
    );
  };

  return (
    <PageContainer>
      <PageContent maxWidth="100%">
        <DecemberDoorsBand />
        <CtaBanner />
        <HealthBingoModal
          isOpen={showHealthBingo && !modalDismissed}
          onDismiss={() => setModalDismissed(true)}
        />
        {renderTopSection()}
        <div className={styles["home-content-wrapper"]}>
          <div className={styles["home-content"]}>
            {data.learner.screens.home.recommendedForYou?.items.length > 0 &&
              data.learner.screens.index.score?.value && (
                <FeaturedLayout
                  featuredItem={
                    data.learner.screens.home.recommendedForYou
                      .items[0] as LearnerRecording
                  }
                  secondaryItems={
                    data.learner.screens.home.recommendedForYou.items
                      .slice(1, 3)
                      .filter(Boolean) as LearnerRecording[]
                  }
                  subtitle={lowestPillarLabel}
                />
              )}
            {data.learner.screens.home.trendingNow?.items.length > 0 && (
              <PopularContent
                items={data.learner.screens.home.trendingNow.items}
              />
            )}
            {data.learner.screens.home.upcomingLiveSessions.items.length >
              0 && (
              <DatedList
                items={
                  data.learner.screens.home.upcomingLiveSessions
                    .items as LearnerGroupSession[]
                }
              />
            )}
            <div className={styles["quote-container"]}>
              <LotusIcon />
              <span className={styles["quote-title"]}>
                "{randomQuote.title}"
              </span>
              <span>{randomQuote.author}</span>
            </div>
          </div>
        </div>
      </PageContent>
    </PageContainer>
  );
};

export default Home;
